import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { ControlGuidance } from '@/lib/control-guidance';

/**
 * ⚠ THE CONTROL LIBRARY — browse and add, WITHOUT a framework.
 *
 * A Compliance licence entitles a tenant to the whole published VeriSAQ canonical library
 * (D-COMPLIANCE-LICENSE-GRANTS-WHOLE-CANONICAL-LIBRARY). Framework licensing and adoption are a
 * SEPARATE layer: they bring a framework's requirements and its mapped controls, and they are
 * not a prerequisite for adding a control. Until this slice, adoption was the only door into the
 * workspace — not by design, but because no other door had been built.
 */

/** ⚠ `in_workspace` travels WITH the row so the page never reconciles two lists. */
export interface LibraryControl {
  control_reference: string;
  display_code: string;
  title: string;
  description: string | null;
  domain_public_id: string;
  domain_name: string;
  family_public_id: string;
  family_name: string;
  in_workspace: boolean;
}

/**
 * ONE library control, for the detail window: the list row plus the three authored guidance fields.
 * ⚠ Required-and-nullable: NULL means "not authored" and the window hides the field. The list rows stay lean —
 *   these travel only on the single-control read.
 */
export interface LibraryControlDetail extends LibraryControl, ControlGuidance {}

export interface LibraryList {
  rows: LibraryControl[];
  total: number;
  limit: number;
  offset: number;
}

export interface LibraryQuery {
  search?: string;
  domain?: string;
  family?: string;
  limit?: number;
  offset?: number;
}

export function useControlLibrary(params: LibraryQuery = {}) {
  const { tenantId } = useAuthStore();
  const { search, domain, family, limit = 200, offset = 0 } = params;
  return useQuery({
    queryKey: ['cts-library', tenantId, search ?? '', domain ?? '', family ?? '', limit, offset],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (search) q.set('search', search);
      if (domain) q.set('domain', domain);
      if (family) q.set('family', family);
      q.set('limit', String(limit));
      q.set('offset', String(offset));
      const { data } = await apiClient.get(`/compliance/workspace/controls/library?${q}`);
      return data.data as LibraryList;
    },
    enabled: !!tenantId,
    // ⚠ Typing in the search box must not blank the grid between keystrokes — an empty result
    //    that is really "still loading" reads as "no controls match", which is one of the three
    //    empty states and would be a lie.
    placeholderData: keepPreviousData,
  });
}

/**
 * ONE library control — the detail window's read.
 *
 * ⚠ The server answers it from the list's own query narrowed to one reference (same eligibility, same "in my
 *   workspace"), so the window and its row cannot disagree. 404 for anything the library does not offer.
 * ⚠ KEYED UNDER 'cts-library', so the Add mutation's invalidation of the library refreshes an open window too:
 *   the button flips to "In workspace" from the server's answer, not from a client-side guess.
 * ⓘ A 404 is an ANSWER, not a fault: it resolves to `null` ("the library does not offer this") and is neither
 *   retried nor rendered as an error. Anything else still fails as an error.
 */
export function useLibraryControl(reference: string | undefined) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-library', tenantId, 'detail', reference ?? ''],
    queryFn: async (): Promise<LibraryControlDetail | null> => {
      try {
        const { data } = await apiClient.get(
          `/compliance/workspace/controls/library/${encodeURIComponent(reference as string)}`);
        return data.data as LibraryControlDetail;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tenantId && !!reference,
  });
}

/**
 * Add a control to the workspace. ⚠ IDEMPOTENT on the server, so a double-tap is harmless and
 * the optimistic flip below never has to be undone for that reason.
 */
export function useAddControlToWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (controlReference: string) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/controls/${controlReference}/add`, {});
      return data.data;
    },
    onSuccess: () => {
      // ⚠ Both, because the control now exists in two places the user can look at: the library
      //    row must show "in workspace", and My Controls must actually contain it.
      qc.invalidateQueries({ queryKey: ['cts-library'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}
